import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import FloorPlanEditor, { DrawingItem, } from './floorPlanEditor';

// FloorPlanEditorWrapperProps インターフェースを拡張
interface FloorPlanEditorWrapperProps {
    initialData: {
        id?: number;
        title: string;
        json_data?: string | null;
        image_data?: string | null;
        background_image_url?: string | null;
        floor_plan_image_url?: string | null;
    };
    editorRef?: (ref: any) => void; // エディタのrefを取得するコールバック
    onChange?: (data: {
        title: string;
        json_data: string;
        image_data: string | null;
        background_image_url: string | null;
        background_image_base64?: string | null; // Base64データを追加
        floor_plan_image_url?: string | null;
        newBackgroundFile?: File | null;
        backgroundAction?: 'keep' | 'remove' | 'update';
        modified?: boolean;
    }) => void;
}

// 内部処理用のstate型も拡張
interface ProcessedData {
    title: string;
    drawings: DrawingItem[];
    image_data: string | null;
    background_image_url: string | null;
    background_image_base64?: string | null; // Base64データを追加
    floor_plan_image_url: string | null;
    newBackgroundFile: File | null;
    backgroundAction: 'keep' | 'remove' | 'update';
}

// JsonDrawingDataインターフェースの修正
interface JsonDrawingData {
    id?: number;
    title: string;
    drawings: DrawingItem[];
    backgroundImagePath: string | null;
    backgroundImageBase64?: string | null;
}

// ref インターフェースの修正
interface FloorPlanEditorWrapperRef {
    captureCanvas: () => Promise<Blob | null>;
    confirmCurrentLine: () => Promise<boolean>;
    getCurrentJsonData: () => string | null;
    hasBackgroundLoadError: () => boolean;
}

const FloorPlanEditorWrapper = React.memo(({ initialData, editorRef, onChange }: FloorPlanEditorWrapperProps) => {
    const editorComponentRef = useRef<any>(null);
    const [isLegacyMode, setIsLegacyMode] = useState<boolean>(false);
    const [processedData, setProcessedData] = useState<ProcessedData>({
        title: initialData.title || '見取り図',
        drawings: [],
        image_data: null,
        background_image_url: initialData.background_image_url || null,
        background_image_base64: null,
        floor_plan_image_url: initialData.floor_plan_image_url || null,
        newBackgroundFile: null,
        backgroundAction: 'keep'
    });
    const [isDataReady, setIsDataReady] = useState<boolean>(false);

    // refをセットする関数
    const setEditorRef = useCallback((ref: any) => {
        if (!ref) return; // null チェック

        editorComponentRef.current = ref;
        if (editorRef) {
            // editorRef.current にすべてのメソッドを含めて渡す
            const wrapperRef: FloorPlanEditorWrapperRef = {
                captureCanvas: async () => {
                    if (editorComponentRef.current && editorComponentRef.current.captureCanvas) {
                        return await editorComponentRef.current.captureCanvas();
                    }
                    return null;
                },
                confirmCurrentLine: async () => {
                    if (editorComponentRef.current && editorComponentRef.current.confirmCurrentLine) {
                        return await editorComponentRef.current.confirmCurrentLine();
                    }
                    return false;
                },
                getCurrentJsonData: () => {
                    if (editorComponentRef.current && editorComponentRef.current.getCurrentJsonData) {
                        return editorComponentRef.current.getCurrentJsonData();
                    }

                    // エディタ参照がない場合は処理済みデータからJSONを生成
                    const jsonData: JsonDrawingData = {
                        title: processedData.title,
                        drawings: processedData.drawings,
                        backgroundImagePath: processedData.background_image_url
                    };

                    if (processedData.background_image_base64) {
                        jsonData.backgroundImageBase64 = processedData.background_image_base64;
                    }

                    return JSON.stringify(jsonData);
                },
                // 背景画像を読み込めなかったか（保存処理が編集前のまま残すかどうかに使う）
                hasBackgroundLoadError: () => {
                    return Boolean(editorComponentRef.current?.hasBackgroundLoadError?.());
                }
            };

            editorRef(wrapperRef);
        }
    }, [initialData.id, processedData]); // processedDataを依存配列に追加

    // image_dataがあり、background_image_urlとfloor_plan_image_urlがない場合はlegacyモードとして判定
    useEffect(() => {
        const isLegacy = Boolean(
            initialData.image_data &&
            !initialData.background_image_url &&
            !initialData.floor_plan_image_url
        );
        setIsLegacyMode(isLegacy);
    }, [initialData]);

    // 初期データの解析部分（修正版）
    useEffect(() => {
        let isMounted = true; // マウント状態を追跡

        const processInitialData = async () => {
            try {
                let title = initialData.title || '見取り図';
                let drawings: DrawingItem[] = [];
                let background_image_url = initialData.background_image_url || null;
                let background_image_base64 = null;
                let floor_plan_image_url = initialData.floor_plan_image_url || null;

                // JSON データの解析
                if (initialData.json_data) {
                    try {
                        const parsed = JSON.parse(initialData.json_data);

                        // 'drawings'プロパティを持っている場合（オブジェクト形式）
                        if (parsed.drawings && Array.isArray(parsed.drawings)) {
                            drawings = parsed.drawings;
                            title = parsed.title || title;

                            // backgroundImagePath があれば設定
                            if (parsed.backgroundImagePath && !background_image_url) {
                                background_image_url = parsed.backgroundImagePath;
                            }

                            // backgroundImageBase64 があれば設定（一時的な画像データ）
                            if (parsed.backgroundImageBase64) {
                                background_image_base64 = parsed.backgroundImageBase64;
                            }
                        }
                        // 配列の場合は直接使用
                        else if (Array.isArray(parsed)) {
                            drawings = parsed;
                        }
                        // その他のオブジェクト形式の場合は空配列
                        else {
                            console.warn('Unexpected JSON format:', parsed);
                        }
                    } catch (e) {
                        console.error('Error parsing JSON data:', e);
                    }
                }

                // コンポーネントがアンマウントされていないかチェック
                if (!isMounted) return;

                // 状態を更新
                setProcessedData({
                    title,
                    drawings,
                    image_data: initialData.image_data || null,
                    background_image_url,
                    background_image_base64,
                    floor_plan_image_url,
                    newBackgroundFile: null,
                    backgroundAction: 'keep'
                });

                // データ準備完了
                setIsDataReady(true);
            } catch (error) {
                console.error("Error processing initialData:", error);
                if (isMounted) {
                    setIsDataReady(true); // エラーでも準備完了とする
                }
            }
        };

        processInitialData();

        // クリーンアップ関数
        return () => {
            isMounted = false;
        };
    }, [initialData]);

    // FloorPlanEditorからのデータ変更を処理するコールバック関数
    const handleEditorChange = useCallback((data: {
        id?: number;
        title: string;
        drawings: DrawingItem[];
        backgroundImage: HTMLImageElement | null;
        backgroundImageBase64: string | null;
        backgroundImageUrl: string | null;
        action: 'update' | 'clearBackground' | 'newBackground';
        newBackgroundFile?: File | null;
    }) => {
        // 背景画像のアクションを決定
        let backgroundAction: 'keep' | 'remove' | 'update' = 'keep';
        let newBackgroundFile: File | null = null;
        let background_image_url = data.backgroundImageUrl;
        let background_image_base64 = data.backgroundImageBase64;

        // 変更があったかどうかを示すフラグ（デフォルトはfalse）
        let modified = false;

        // 背景画像の変更を検出
        if (data.action === 'clearBackground') {
            backgroundAction = 'remove';
            background_image_url = null;
            background_image_base64 = null;
            modified = true; // 変更あり
        } else if (data.action === 'newBackground' && data.newBackgroundFile) {
            backgroundAction = 'update';
            newBackgroundFile = data.newBackgroundFile;
            // 新しい画像が選択された場合
            background_image_url = null;
            background_image_base64 = data.backgroundImageBase64; // Base64データを保持
            modified = true; // 変更あり
        } else if (data.backgroundImageUrl !== processedData.background_image_url) {
            // 背景画像URLが変わった場合も変更ありとマーク
            modified = true;
        } else if (JSON.stringify(data.drawings) !== JSON.stringify(processedData.drawings)) {
            // 描画内容が変わった場合も変更ありとマーク（マーカーや図形の追加/変更）
            modified = true;
        }

        // ディープコピーを使用してstateを更新
        const newDrawings = JSON.parse(JSON.stringify(data.drawings)); // ディープコピー

        setProcessedData(prevState => ({
            ...prevState,
            title: data.title,
            drawings: newDrawings,
            image_data: prevState.image_data, // image_dataは変更しない
            background_image_url,
            background_image_base64,
            floor_plan_image_url: prevState.floor_plan_image_url,
            newBackgroundFile,
            backgroundAction
        }));

        // 親コンポーネントに変更を通知
        if (onChange) {
            // JSON形式に変換して親に通知
            // 描画データを含むJSONデータを作成
            const jsonData: JsonDrawingData = {
                id: initialData.id,  // IDを含める
                title: data.title,
                drawings: JSON.parse(JSON.stringify(data.drawings)), // ディープコピー
                backgroundImagePath: background_image_url
            };

            // 一時的にBase64データをJSON内に含める
            if (background_image_base64) {
                jsonData.backgroundImageBase64 = background_image_base64;
            }

            // JSONデータをシリアライズ
            const jsonString = JSON.stringify(jsonData);

            const updateData = {
                title: data.title,
                json_data: jsonString, // シリアライズしたJSONデータ
                image_data: processedData.image_data, // image_dataは変更しない
                background_image_url,
                background_image_base64,
                floor_plan_image_url: processedData.floor_plan_image_url,
                newBackgroundFile,
                backgroundAction,
                modified
            };

            onChange(updateData);
        }
    }, [onChange, processedData, initialData.id]);

    // image_dataのみの場合は表示専用コンポーネントを返す
    if (isLegacyMode && initialData.image_data) {
        return (
            <Box className="floor-plan-editor-legacy-wrapper">
                <Paper elevation={3} sx={{ width: 600, height: 250, position: 'relative', overflow: 'hidden', margin: '0 auto' }}>
                    <img
                        src={`data:image/jpeg;base64,${initialData.image_data}`}
                        alt={initialData.title || '見取り図'}
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                </Paper>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    align="center"
                    sx={{
                        display: 'block',
                        mt: 1,
                        backgroundColor: '#f5f5f5',
                        padding: '4px 8px',
                        borderRadius: '4px'
                    }}
                >
                    ※旧形式の見取り図データのため編集できません。新しい見取り図を作成するには、新規に追加してください。
                </Typography>
            </Box>
        );
    }

    // データ準備が完了していない場合はローディング表示
    if (!isDataReady) {
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '300px',
                borderRadius: 1,
                overflow: 'hidden',
                border: 1,
                borderColor: 'divider',
                justifyContent: 'center',
                alignItems: 'center'
            }}>
                <Typography variant="body2" color="textSecondary">
                    見取り図データを処理中...
                </Typography>
            </Box>
        );
    }

    return (
        <div className="floor-plan-editor-wrapper">
            <FloorPlanEditor
                ref={setEditorRef}
                initialData={{
                    id: initialData.id, // 明示的にidを渡す
                    title: processedData.title,
                    drawings: processedData.drawings,
                    image_data: processedData.image_data,
                    background_image_url: processedData.background_image_url,
                    background_image_base64: processedData.background_image_base64
                }}
                onDataChange={handleEditorChange}
                key={`editor-${initialData.id}-${isDataReady}`} // isDataReadyもキーに含める
            />
        </div>
    );
}, (prevProps, nextProps) => {
    // 最適化のためのカスタム比較関数
    // IDが同じで、JSONデータが同じなら再レンダリングしない
    return (
        prevProps.initialData.id === nextProps.initialData.id &&
        prevProps.initialData.json_data === nextProps.initialData.json_data &&
        prevProps.initialData.background_image_url === nextProps.initialData.background_image_url &&
        prevProps.initialData.floor_plan_image_url === nextProps.initialData.floor_plan_image_url
    );
});

export default FloorPlanEditorWrapper;